(() => {
  const qs = (s, c = document) => c.querySelector(s);
  const qsa = (s, c = document) => [...c.querySelectorAll(s)];
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const syncBodyLock = () => {
    const anyOpen = Boolean(qs('.mobile-drawer.is-open, .cart-drawer.is-open'));
    document.body.classList.toggle('drawer-open', anyOpen);
  };

  const reveal = () => {
    const items = qsa('[data-reveal]');
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) {
      items.forEach(i => i.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: .08, rootMargin: '0px 0px -18px 0px' });
    items.forEach(i => io.observe(i));
  };

  const stickyHeader = () => {
    const header = qs('[data-site-header]');
    if (!header) return;
    let ticking = false;
    const update = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 8);
      ticking = false;
    };
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }, { passive: true });
    update();
  };

  const smoothImages = (context = document) => {
    qsa('.lq-smooth-image, .lq-gallery-image', context).forEach(img => {
      if (img.dataset.imageReadyBound === 'true') return;
      img.dataset.imageReadyBound = 'true';

      const show = () => requestAnimationFrame(() => img.classList.add('is-loaded'));
      if (img.complete && img.naturalWidth > 0) {
        if (typeof img.decode === 'function') img.decode().catch(() => {}).finally(show);
        else show();
      } else {
        img.addEventListener('load', show, { once: true });
        img.addEventListener('error', show, { once: true });
      }
    });
  };

  const drawer = () => {
    const shell = qs('[data-mobile-drawer]');
    if (!shell) return;
    const openButton = qs('[data-drawer-open]');
    const closes = qsa('[data-drawer-close]', shell);
    const panel = qs('.mobile-drawer__panel', shell);
    let lastFocus = null;

    const set = state => {
      shell.classList.toggle('is-open', state);
      shell.setAttribute('aria-hidden', state ? 'false' : 'true');
      openButton?.setAttribute('aria-expanded', state ? 'true' : 'false');
      syncBodyLock();
      if (state) {
        lastFocus = document.activeElement;
        setTimeout(() => qs('[data-drawer-close].mobile-drawer__close', shell)?.focus(), 80);
      } else if (lastFocus instanceof HTMLElement) {
        lastFocus.focus({ preventScroll: true });
      }
    };

    openButton?.addEventListener('click', () => set(true));
    closes.forEach(el => el.addEventListener('click', () => set(false)));
    qsa('.mobile-drawer__nav a, .mobile-drawer__shop', shell).forEach(link => link.addEventListener('click', () => set(false)));
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && shell.classList.contains('is-open')) set(false);
    });

    panel?.addEventListener('click', e => e.stopPropagation());
  };

  const gallery = () => {
    qsa('[data-product-gallery]').forEach(g => {
      const stage = qs('[data-gallery-stage]', g);
      const img = qs('.product-gallery__image', g);
      qsa('[data-gallery-thumb]', g).forEach(btn => btn.addEventListener('click', async () => {
        if (!img || !stage || btn.classList.contains('is-active')) return;
        qsa('[data-gallery-thumb]', g).forEach(b => b.classList.remove('is-active'));
        btn.classList.add('is-active');
        stage.classList.add('is-switching');

        const nextSrc = btn.dataset.imageSrc;
        const preload = new Image();
        preload.src = nextSrc;
        try {
          if (typeof preload.decode === 'function') await preload.decode();
          else await new Promise((resolve, reject) => {
            preload.onload = resolve;
            preload.onerror = reject;
          });
        } catch (_) {
          stage.classList.remove('is-switching');
          return;
        }

        img.classList.remove('is-loaded');
        img.src = nextSrc;
        img.alt = btn.dataset.imageAlt || '';
        try { if (typeof img.decode === 'function') await img.decode(); } catch (_) {}
        requestAnimationFrame(() => {
          img.classList.add('is-loaded');
          stage.classList.remove('is-switching');
        });
      }));
    });
  };

  const variantSelect = () => {
    qsa('[data-variant-select]').forEach(select => {
      select.addEventListener('change', () => {
        const option = select.options[select.selectedIndex];
        const form = select.closest('form');
        const idInput = qs('input[name="id"]', form);
        if (idInput) idInput.value = option.value;
        const button = qs('[data-add-to-cart]', form);
        if (button) {
          const available = option.dataset.available === 'true';
          button.disabled = !available;
          button.textContent = available ? 'Add to bag' : 'Sold out';
        }
        const price = qs('[data-product-price]');
        if (price && option.dataset.price) price.textContent = option.dataset.price;
      });
    });
  };

  const quantity = () => {
    qsa('.lq5-qty').forEach(group => {
      const input = qs('[data-qty-input]', group);
      const minus = qs('[data-qty-minus]', group);
      const plus = qs('[data-qty-plus]', group);
      if (!input) return;
      minus?.addEventListener('click', () => {
        const min = Number(input.min || 1);
        input.value = Math.max(min, Number(input.value || 1) - 1);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      plus?.addEventListener('click', () => {
        input.value = Number(input.value || 1) + 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  };

  const accordions = () => {
    qsa('[data-lq5-accordion]').forEach(group => {
      const items = qsa('[data-accordion-item]', group);
      const close = item => {
        item.classList.remove('is-open');
        qs('[data-accordion-trigger]', item)?.setAttribute('aria-expanded', 'false');
      };
      const open = item => {
        item.classList.add('is-open');
        qs('[data-accordion-trigger]', item)?.setAttribute('aria-expanded', 'true');
      };

      items.forEach(item => {
        const trigger = qs('[data-accordion-trigger]', item);
        trigger?.addEventListener('click', () => {
          const wasOpen = item.classList.contains('is-open');
          items.forEach(other => { if (other !== item) close(other); });
          if (wasOpen) close(item); else open(item);
        });
      });
    });
  };

  const updateCartCount = count => {
    qsa('[data-cart-count]').forEach(el => {
      el.textContent = count;
      el.classList.toggle('is-empty', Number(count) === 0);
    });
    qsa('[data-cart-drawer-count]').forEach(el => { el.textContent = `(${count})`; });
  };

  const escapeHtml = value => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const formatEGP = cents => {
    const amount = Number(cents || 0) / 100;
    const hasDecimals = Math.round(amount * 100) % 100 !== 0;
    return `${new Intl.NumberFormat('en-EG', {
      minimumFractionDigits: hasDecimals ? 2 : 0,
      maximumFractionDigits: 2
    }).format(amount)} EGP`;
  };

  let cartDrawerApi = null;

  const cartDrawer = () => {
    const shell = qs('[data-cart-drawer]');
    if (!shell) return null;
    const panel = qs('.cart-drawer__panel', shell);
    const body = qs('[data-cart-drawer-body]', shell);
    const footer = qs('[data-cart-drawer-footer]', shell);
    const subtotal = qs('[data-cart-subtotal]', shell);
    const openers = qsa('[data-cart-drawer-open]');
    const closers = qsa('[data-cart-drawer-close]', shell);
    let lastFocus = null;
    let busy = false;

    const setOpen = state => {
      shell.classList.toggle('is-open', state);
      shell.setAttribute('aria-hidden', state ? 'false' : 'true');
      syncBodyLock();
      if (state) {
        lastFocus = document.activeElement;
        setTimeout(() => panel?.focus({ preventScroll: true }), 80);
      } else if (lastFocus instanceof HTMLElement) {
        lastFocus.focus({ preventScroll: true });
      }
    };

    const render = cart => {
      updateCartCount(cart.item_count || 0);
      if (subtotal) subtotal.textContent = formatEGP(cart.total_price);
      if (footer) footer.hidden = !cart.item_count;
      if (!body) return;

      if (!cart.item_count) {
        body.innerHTML = `
          <div class="cart-drawer__empty" data-cart-empty>
            <span class="cart-drawer__empty-mark">Q</span>
            <h3>Your bag is empty.</h3>
            <p>Five focused formulas. Start with the one that fits your routine.</p>
            <a class="button" href="${escapeHtml(root())}collections/all">Shop the collection</a>
          </div>`;
        return;
      }

      body.innerHTML = `<div class="cart-drawer__items" data-cart-items>${cart.items.map(item => {
        const variant = item.variant_title && item.variant_title !== 'Default Title'
          ? `<p class="cart-drawer__variant">${escapeHtml(item.variant_title)}</p>` : '';
        const image = item.image
          ? `<img class="cart-drawer__product-image" src="${escapeHtml(item.image)}" alt="${escapeHtml(item.product_title)}" loading="eager" decoding="async">` : '';
        return `
          <article class="cart-drawer__item" data-cart-key="${escapeHtml(item.key)}">
            <a class="cart-drawer__image" href="${escapeHtml(item.url)}">${image}</a>
            <div class="cart-drawer__item-main">
              <div class="cart-drawer__item-top">
                <a class="cart-drawer__title" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>
                <span class="cart-drawer__price">${formatEGP(item.final_line_price)}</span>
              </div>
              ${variant}
              <div class="cart-drawer__item-actions">
                <div class="cart-drawer__qty" aria-label="Quantity for ${escapeHtml(item.product_title)}">
                  <button type="button" data-cart-qty-minus aria-label="Decrease quantity">−</button>
                  <span data-cart-qty>${item.quantity}</span>
                  <button type="button" data-cart-qty-plus aria-label="Increase quantity">+</button>
                </div>
                <button class="cart-drawer__remove" type="button" data-cart-remove>Remove</button>
              </div>
            </div>
          </article>`;
      }).join('')}</div>`;
      smoothImages(body);
    };

    const getCart = async () => {
      const response = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Could not load your bag.');
      return response.json();
    };

    const refresh = async ({ open = false } = {}) => {
      shell.classList.add('is-loading');
      try {
        const cart = await getCart();
        render(cart);
        if (open) setOpen(true);
        return cart;
      } finally {
        shell.classList.remove('is-loading');
      }
    };

    const change = async (key, quantityValue) => {
      if (busy) return;
      busy = true;
      shell.classList.add('is-updating');
      try {
        const response = await fetch(`${root()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity: Math.max(0, Number(quantityValue) || 0) })
        });
        if (!response.ok) throw new Error('Could not update your bag.');
        const cart = await response.json();
        render(cart);
      } catch (error) {
        console.error(error);
      } finally {
        shell.classList.remove('is-updating');
        busy = false;
      }
    };

    openers.forEach(opener => opener.addEventListener('click', async event => {
      event.preventDefault();
      setOpen(true);
      try {
        await refresh();
      } catch (error) {
        setOpen(false);
        window.location.href = opener.href;
      }
    }));

    closers.forEach(closer => closer.addEventListener('click', () => setOpen(false)));

    body?.addEventListener('click', event => {
      const item = event.target.closest('[data-cart-key]');
      if (!item) return;
      const key = item.dataset.cartKey;
      const qty = Number(qs('[data-cart-qty]', item)?.textContent || 1);
      if (event.target.closest('[data-cart-qty-minus]')) change(key, qty - 1);
      if (event.target.closest('[data-cart-qty-plus]')) change(key, qty + 1);
      if (event.target.closest('[data-cart-remove]')) change(key, 0);
    });

    document.addEventListener('keydown', event => {
      if (event.key === 'Escape' && shell.classList.contains('is-open')) setOpen(false);
    });

    return {
      open: () => { setOpen(true); return refresh(); },
      refresh, render, setOpen
    };
  };

  const addToCart = () => {
    qsa('[data-product-form]').forEach(form => {
      form.addEventListener('submit', async event => {
        event.preventDefault();
        const button = qs('[data-add-to-cart]', form);
        const status = qs('[data-form-status]', form);
        if (!button || button.disabled || button.dataset.loading === 'true') return;

        const original = button.textContent.trim();
        button.dataset.loading = 'true';
        button.classList.add('is-loading');
        button.textContent = 'Adding…';
        if (status) status.textContent = 'Adding to your bag…';

        try {
          const response = await fetch(`${root()}cart/add.js`, {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: new FormData(form)
          });
          if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.description || 'Unable to add this item.');
          }

          button.textContent = 'Added';
          button.classList.add('is-added');
          if (status) status.textContent = 'Added to your bag.';

          if (cartDrawerApi) {
            await cartDrawerApi.open();
          } else {
            const cartResponse = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } });
            if (cartResponse.ok) {
              const cart = await cartResponse.json();
              updateCartCount(cart.item_count || 0);
            }
          }

          setTimeout(() => {
            button.textContent = original;
            button.classList.remove('is-added');
          }, 1200);
        } catch (error) {
          button.textContent = 'Try again';
          if (status) status.textContent = error.message || 'Something went wrong. Please try again.';
          setTimeout(() => { button.textContent = original; }, 1700);
        } finally {
          button.dataset.loading = 'false';
          button.classList.remove('is-loading');
        }
      });
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    smoothImages();
    reveal();
    stickyHeader();
    drawer();
    gallery();
    variantSelect();
    quantity();
    accordions();
    cartDrawerApi = cartDrawer();
    addToCart();
  });
})();
